import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    untracked,
} from '@angular/core';
import { DocumentItemPartial, FavoriteFieldEntity } from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import {
    CALLOUT_COLORS,
    resolveCalloutColor,
} from '../../../../../callout-colors';
import { TableViewComponent } from '../../../../../components/table-view/table-view.component';
import {
    cleanTableText,
    DocumentTableValue,
    normalizeTableWidths,
    parseTableValue,
    resizeTableWidths,
    resolveTableStyle,
    resolveTableZebra,
    editorRowPageBreaks,
    sameTableValue,
    TABLE_LIMITS,
    TABLE_STYLES,
    TableStyleKey,
    widthsWithNewColumn,
    widthsWithoutColumn,
} from '../../../../../table-options';
import { CustomColorSwatchComponent } from '../../custom-color-swatch/custom-color-swatch.component';
import { RaEditTooltipDirective } from '../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { CardBaseComponent } from '../_card-base/card-base.component';
import { CardTypes } from '../_card-base/enums/card-types.enum';
import { RaLookPanelDirective } from '../_card-base/look-panel.directive';
import { TextEditingService } from '../../../services/text-editing.service';
import { TableCellTextDirective } from './table-cell-text.directive';
import { UndoToastService } from '../_card-base/undo-toast.service';

/** Menu "Aparência" do rail (cor, estilo e linhas alternadas). */

/**
 * Card da TABELA (item TABLE): a tabela como ela imprime, editável no lugar. Cada célula
 * é texto de uma linha; Tab anda pelas células e Enter desce para a mesma coluna da linha
 * de baixo (criando a linha no fim). No hover, a linha mostra a alça e o remover; o
 * cabeçalho, o remover da coluna. "+ Linha" embaixo e "+" à direita adicionam. Cor,
 * estilo e zebra moram no rail.
 *
 * Salva a tabela INTEIRA (update-table) ao sair de uma célula alterada, ao adicionar,
 * remover e reordenar. Enquanto uma célula tem foco, o eco do servidor não sobrescreve
 * o que está sendo digitado.
 */
@Component({
    selector: 'table-card',
    templateUrl: 'table-card.component.html',
    styleUrl: 'table-card.component.scss',
    imports: [
        CardBaseComponent,
        CustomColorSwatchComponent,
        TableViewComponent,
        TableCellTextDirective,
        RaIconComponent,
        RaEditTooltipDirective,
        RaLookPanelDirective,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        TranslatePipe,
    ],
})
export class TableCardComponent {
    public field = input<DocumentItemPartial>();
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);

    public readonly cardTypes = CardTypes;
    public readonly colorOptions = CALLOUT_COLORS;
    public readonly styleOptions = TABLE_STYLES;
    public readonly limits = TABLE_LIMITS;

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    /** Tamanho do texto da tabela (config textSize, ao vivo enquanto a barra mexe). */
    public textEditing = inject(TextEditingService);
    private _editItemsService = inject(EditItemsService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);
    private _undoToast = inject(UndoToastService);

    /** Tabela em edição (cópia local; o servidor recebe no commit). */
    public table = signal<DocumentTableValue>({ columns: [], rows: [] });

    /** Alguma célula tem foco: o eco do servidor espera. */
    private _focused = signal(false);

    public currentField = computed(() => this.getCurrentField());
    /** Cor livre sendo arrastada no seletor do menu: o card mostra na hora, sem salvar. */
    public colorPreview = signal<string | null>(null);
    public color = computed(() =>
        resolveCalloutColor(this.colorPreview() ?? this.currentField()?.configs?.calloutColor)
    );
    public styleKey = computed(() => resolveTableStyle(this.currentField()?.configs?.tableStyle));
    public zebra = computed(() => resolveTableZebra(this.currentField()?.configs?.tableZebra));
    public canAddRow = computed(() => this.table().rows.length < TABLE_LIMITS.MAX_ROWS);
    public canAddColumn = computed(() => this.table().columns.length < TABLE_LIMITS.MAX_COLUMNS);
    public canRemoveColumn = computed(() => this.table().columns.length > TABLE_LIMITS.MIN_COLUMNS);

    /**
     * Onde a folha do PDF vira no meio da tabela: linha do EDITOR => número da página
     * nova. A prévia conta só as linhas impressas (o PDF pula as vazias), então a conversão
     * anda pelas linhas com conteúdo.
     */
    public rowPageBreaks = computed(() => {
        const id = this.currentField()?._id;
        return editorRowPageBreaks(
            this.table().rows,
            id ? this.editDataService.pdfRowBreaksByItemId()[id] : undefined
        );
    });

    public blocked = computed(
        () =>
            this.editService.loading() ||
            this.editDataService.currentSessionIsDisabled() ||
            this.editDataService.documentIsBlocked()
    );

    constructor() {
        inject(DestroyRef).onDestroy(() => this._stopResize?.());

        // Servidor (ou realtime) mudou a tabela: adota, a não ser que o usuário esteja
        // digitando numa célula ou arrastando uma coluna agora.
        effect(() => {
            const serverTable = parseTableValue(this.currentField()?.value);
            if (untracked(this._focused) || untracked(this.resizing) !== null) return;
            untracked(() => {
                if (!sameTableValue(serverTable, this.table())) this.table.set(serverTable);
            });
        });
    }

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }

    // #region * [Edição das células]

    public onHeaderInput(column: number, text: string): void {
        this.table.update(t => ({
            ...t,
            columns: t.columns.map((c, i) => (i === column ? text : c)),
        }));
    }

    public onCellInput(row: number, column: number, text: string): void {
        this.table.update(t => ({
            ...t,
            rows: t.rows.map((r, ri) =>
                ri === row ? r.map((c, ci) => (ci === column ? text : c)) : r
            ),
        }));
    }

    /**
     * Enter desce para a mesma coluna da linha de baixo (o cabeçalho desce para a primeira
     * linha). Na última linha, cria uma nova, como numa planilha.
     */
    public onCellKeydown(event: KeyboardEvent, row: number, column: number): void {
        if (event.key !== 'Enter') return;
        event.preventDefault();

        const next = row + 1;
        if (next >= this.table().rows.length) {
            if (!this.canAddRow()) return;
            this.addRow(false);
        }

        setTimeout(() => this.focusCell(next, column));
    }

    public onFocusIn(): void {
        this._focused.set(true);
    }

    /** Saiu de uma célula: grava se mudou. Saiu da tabela inteira: libera o eco. */
    public onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget as Node | null;
        if (!next || !this._host.nativeElement.contains(next)) this._focused.set(false);
        this.commit();
    }

    // #endregion

    // #region * [Linhas e colunas]

    public addRow(focus = true): void {
        if (this.blocked() || !this.canAddRow()) return;

        this.table.update(t => ({ ...t, rows: [...t.rows, t.columns.map(() => '')] }));
        this.commit();

        if (focus) {
            const row = this.table().rows.length - 1;
            setTimeout(() => this.focusCell(row, 0));
        }
    }

    /** Remove na hora; o toast oferece Desfazer, que reinsere a linha onde estava. */
    public removeRow(row: number): void {
        if (this.blocked()) return;
        const removed = this.table().rows[row];
        if (removed === undefined) return;

        this.table.update(t => ({ ...t, rows: t.rows.filter((_, i) => i !== row) }));
        this.commit();

        this._undoToast.show('document.edit.tableCard.rowRemoved', () => {
            if (this.blocked()) return;
            this.table.update(t => {
                const rows = [...t.rows];
                // A linha volta com o número de colunas de agora.
                const cells = t.columns.map((_, i) => removed[i] ?? '');
                rows.splice(Math.min(row, rows.length), 0, cells);
                return { ...t, rows };
            });
            this.commit();
        });
    }

    public addColumn(): void {
        if (this.blocked() || !this.canAddColumn()) return;

        this.table.update(t => ({
            columns: [...t.columns, ''],
            rows: t.rows.map(r => [...r, '']),
            ...(t.widths ? { widths: widthsWithNewColumn(t.widths) } : {}),
        }));
        this.commit();

        const column = this.table().columns.length - 1;
        setTimeout(() => this.focusCell(-1, column));
    }

    public removeColumn(column: number): void {
        if (this.blocked() || !this.canRemoveColumn()) return;

        this.table.update(t => ({
            columns: t.columns.filter((_, i) => i !== column),
            rows: t.rows.map(r => r.filter((_, i) => i !== column)),
            ...(t.widths ? { widths: widthsWithoutColumn(t.widths, column) } : {}),
        }));
        this.commit();
    }

    public dropRow(event: CdkDragDrop<string[][]>): void {
        if (event.previousIndex === event.currentIndex) return;

        const rows = [...this.table().rows];
        moveItemInArray(rows, event.previousIndex, event.currentIndex);
        this.table.update(t => ({ ...t, rows }));
        this.commit();
    }

    /** Grava a tabela se ela mudou em relação ao que o campo tem. */
    public commit(): void {
        const field = this.currentField();
        if (!field?._id || this.type() !== CardTypes.DEFAULT || this.blocked()) return;

        const current = this.table();
        const next: DocumentTableValue = {
            columns: current.columns.map(c => cleanTableText(c, TABLE_LIMITS.MAX_HEADER_LENGTH)),
            rows: current.rows.map(r =>
                r.map(c => cleanTableText(c, TABLE_LIMITS.MAX_CELL_LENGTH))
            ),
            ...(current.widths && current.widths.length === current.columns.length
                ? { widths: normalizeTableWidths(current.widths) }
                : {}),
        };
        if (sameTableValue(next, parseTableValue(field.value))) return;

        this.table.set(next);
        this._editItemsService.updateTable(field._id, next);
    }

    // #region * [Largura das colunas]

    /** Arrasto em curso na borda de uma coluna (null = nenhum). */
    public resizing = signal<number | null>(null);
    private _stopResize: (() => void) | null = null;

    /**
     * Arrastar a borda entre a coluna e a seguinte. Uma ganha o que a outra perde; a tabela
     * mostra ao vivo e grava ao soltar. Se ainda não havia larguras (colunas pelo
     * conteúdo), parte das larguras que o navegador desenhou, para nada pular no clique.
     */
    public startResize(event: PointerEvent, column: number): void {
        if (this.blocked() || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();

        const handle = event.currentTarget as HTMLElement;
        const table = handle.closest('table');
        if (!table) return;

        const tableWidth = table.getBoundingClientRect().width;
        const start =
            this.table().widths ??
            normalizeTableWidths(
                Array.from(table.querySelectorAll<HTMLElement>('thead th')).map(
                    th => th.getBoundingClientRect().width
                )
            );
        if (start.length !== this.table().columns.length || tableWidth <= 0) return;

        const startX = event.clientX;
        this.resizing.set(column);

        const onMove = (move: PointerEvent): void => {
            const deltaPct = ((move.clientX - startX) / tableWidth) * 100;
            const widths = resizeTableWidths(start, column, deltaPct);
            this.table.update(t => ({ ...t, widths }));
        };
        const onUp = (): void => {
            this._stopResize?.();
            this.commit();
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        this._stopResize = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            this._stopResize = null;
            this.resizing.set(null);
        };
    }

    /** Duplo clique na borda: as colunas voltam a se ajustar ao conteúdo. */
    public resetWidths(event: MouseEvent): void {
        event.stopPropagation();
        if (this.blocked() || !this.table().widths) return;

        this.table.update(t => ({ columns: t.columns, rows: t.rows }));
        this.commit();
    }

    // #endregion

    /** Foca uma célula; linha -1 é o cabeçalho. */
    public focusCell(row: number, column: number): void {
        const selector =
            row < 0 ? `th [data-cell="h-${column}"]` : `td [data-cell="${row}-${column}"]`;
        this._host.nativeElement.querySelector<HTMLElement>(selector)?.focus();
    }

    // #endregion

    // #region * [Painel de design: cor, estilo, zebra]

    /** Família (`blue`...) ou cor livre (`#rrggbb`). */
    public selectColor(color: string): void {
        this._saveConfig({ calloutColor: color });
    }

    /** O seletor livre fechou: salva o #rrggbb (otimista, como as famílias) e larga a prévia. */
    public chooseCustomColor(hex: string): void {
        this.colorPreview.set(null);
        this.selectColor(hex);
    }

    public selectStyle(style: TableStyleKey): void {
        this._saveConfig({ tableStyle: style });
    }

    public toggleZebra(): void {
        if (this.blocked()) return;
        this._saveConfig({ tableZebra: !this.zebra() });
    }

    private _saveConfig(change: {
        calloutColor?: string;
        tableStyle?: string;
        tableZebra?: boolean;
    }): void {
        // O menu fica aberto: cor, estilo e linhas se ajustam juntos, vendo a tabela mudar.
        const field = this.currentField();
        if (!field?._id) return;

        this._editItemsService.updateItemConfig(field._id, { ...(field.configs ?? {}), ...change });
    }

    // #endregion
}
